import { Avatar, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material'
import { Modal } from 'Components/Modal'
import { sendPost } from 'hooks/use-fetch'
import { useState } from 'react'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import BusinessIcon from '@mui/icons-material/Business'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import { FormatDateTimeValue } from 'core/Utilities'
import { TextAreaWithLabel } from 'Components/TextArea'
import { type ProgramVerificationIncomeApproval, type postError } from 'core/types'
import { toast } from 'react-toastify'
import { useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import { FormatListBulleted } from '@mui/icons-material'
import AccountTreeIcon from '@mui/icons-material/AccountTree'

export interface IncomeApprovalCardProps {
  approval: ProgramVerificationIncomeApproval
  canEdit: boolean
  approvalType?: 'overIncome' | 'waiver'
}

export function IncomeApprovalCard (props: IncomeApprovalCardProps): JSX.Element {
  const [reapply, setReapply] = useState<boolean>(false)
  const [reapplyNotes, setReapplyNotes] = useState<string>('')

  const dispatchRefresh = useRefreshDispatch()
  const refresh = (): void => { dispatchRefresh({ type: 'refresh' }) }

  const details = props.approval

  let iconColor = '#4DA4A4 !important'
  let chipBGColor = 'rgba(77, 164, 164, 0.1)'
  let borderColor = '#4DA4A4'
  let panelBGcolor = '#F6FAFA'
  let status = 'Approved'
  if (details.approved === null) {
    status = 'Pending'
    iconColor = '#722A82 !important'
    chipBGColor = '#EBE0ED'
    borderColor = '#722A82'
    panelBGcolor = '#F8F4F8'
  } else if (!details.approved) {
    status = 'Declined'
    iconColor = '#BC3656 !important'
    chipBGColor = 'rgba(188, 54, 86, 0.1)'
    borderColor = '#BC3656'
    panelBGcolor = '#FBF5F6'
  }

  const reapplyClick = (): void => {
    setReapply(true)
  }

  const confirmReapplyClick = async (): Promise<void> => {
    const submitData = { id: details.incomeApprovalID, responseNotes: reapplyNotes }
    const { response: rsp } = await sendPost('/IncomeApproval/Reapply', submitData)
    const result: { newRecordID: number, errors: postError[], success: boolean } = rsp
    if (!result.success) {
      result.errors.forEach(e => toast.error(e.error))
    } else {
      setReapply(false)
      refresh()
    }
  }

  const declineDetails = (title: string): JSX.Element => {
    return (details?.decidingUserName != null
      ? <div style={{ margin: '10px 0 0 10px' }}>
        <div style={{ fontWeight: 600, marginBottom: 5 }}>{title}</div>
        <Grid container flexDirection='row' spacing={1}>
          <Grid item>
            <Avatar sx={{ backgroundColor: 'rgba(113, 54, 188, 1)', height: '30px', width: '30px' }} >{details.decidingUserName[0]}</Avatar>
          </Grid>
          <Grid item>
            <div style={{ fontWeight: 600 }} >{details.decidingUserName}</div>
            <div>{details.userOrganization}</div>
            <div>{details.whenDecided !== null ? FormatDateTimeValue(details.whenDecided) : ''}</div>
            <div>{details.reason}</div>
            <div>{details.notes}</div>
          </Grid>
        </Grid>
        {details.reasonID === 4 && (<>
          <Grid container flexDirection='row' spacing={0}>
            <Grid item style={{ verticalAlign: 'middle', display: 'inline-block' }}><AccountTreeIcon fontSize='small' /></Grid>
            <Grid item style={{ marginLeft: '10px', marginRight: '10px' }}>Head Start is enrolling this child</Grid>
          </Grid>
        </>)}
      </div>
      : <></>
    )
  }

  return <Box key={details.incomeApprovalID} borderRadius={3} bgcolor={panelBGcolor} border={borderColor}
    sx={{ borderStyle: 'solid', borderWidth: '2px', padding: '10px', marginTop: '5px', borderRadius: '16px' }}
  >
    <Grid container flexDirection='row' spacing={0}>
      <Grid item style={{ verticalAlign: 'middle', display: 'inline-block' }}><FormatListBulleted fontSize='small' /></Grid>
      <Grid item style={{ marginLeft: '10px', marginRight: '10px' }}>{details.type === 'overIncome' ? 'Over Income Approval' : 'Waiver Approval'}</Grid>
      <Grid item>
        <Chip sx={{
          fontSize: '18px',
          height: '24px',
          p: '14px 6px',
          color: iconColor,
          backgroundColor: chipBGColor,
          fontWeight: '600',
          float: 'right'
        }} icon={<FiberManualRecordIcon sx={{ color: iconColor, height: '12px', width: '12px' }} />} label={status}
        />
      </Grid>
    </Grid>
    {details.type === 'waiver' &&
      <Stack direction='column' spacing={1} sx={{ marginTop: '16px' }}>
        <Stack direction='row' spacing={1}>
          <BusinessIcon />
          <Typography variant='body1'>{details.approvingProviderName}</Typography>
        </Stack>
        {(details.approvingProviderPhone ?? '') !== '' && <Stack direction='row' spacing={1}>
          <PhoneIcon />
          <Typography variant='body1'>{details.approvingProviderPhone}</Typography>
        </Stack>
        }
        {(details.approvingProviderEmail ?? '') !== '' && <Stack direction='row' spacing={1}>
          <EmailIcon />
          <Typography variant='body1'>{details.approvingProviderEmail}</Typography>
        </Stack>
        }
      </Stack>
    }
    {details.decidingUserName != null &&
      <>{declineDetails('Response')}</>}
    {props.canEdit && <div style={{ margin: '5px 0 10px 0', width: '100%', textAlign: 'center' }}>
      <Button variant='text' onClick={reapplyClick} >Re-apply</Button>
    </div>}

    <Modal open={reapply}
      confirmButtonText='Re-apply'
      cancelButtonText='Cancel'
      titleContent={<div className='modalTitle'>Re-Apply for Over Income</div>}
      onClose={() => { setReapply(false) }}
      confirmationContent={<>
        <TextAreaWithLabel name='reapplyNotes'
          label='Request Notes'
          value={reapplyNotes}
          onChange={(e) => { setReapplyNotes(e.target.value) }}
          toolTip='Please outline any changes to this request that would mitigate the concerns in the denial.'
        />
        {declineDetails('Previous Notes')}
      </>}
      onConfirm={confirmReapplyClick}
    />

  </Box>
}
